import type { GetServerSideProps } from 'next';
import { ConfigProvider } from 'antd';
import { Locale } from '@fixter/i18n';
import { queryCmsContent } from '$server/modules/cms';
import deferHydration from '$util/deferHydration';
import MainLayout from '$layout/MainLayout';
import TopSection from '$components/pages/about-us/TopSection';
import { QUERY_GET_FOOTER_CMS_CONTENT } from '$components/layout/Footer/gql/queries/getFooterCmsContent';
import { QUERY_READ_FOOTER_DATA } from '$components/layout/Footer/gql/queries/readFooterData';
import { getReassuranceQuery } from '$components/composite/Reassurance/gql';
import { getTopNavBannerQuery } from '$components/pages/checkout/quotes/TopNavBannerWithRedeem/gql';
import { getNavbarQuery } from '$commons/gql';
import { getVrmLookupQuery } from '$quantum/molecules/VrmLookup/gql';
import { getTeamSupportSection } from '$quantum/organisms/TeamSupportSection/gql';
import { GET_CUSTOMER_SERVICE_CMS_QUERY } from '$cms/gql';
import { GET_HOMEPAGE_OPEN_GRAPH_CONTENT, GET_DOMAIN_VERIFICATION_CONTENT } from '$layout/MainLayout/gql';
import { getFeaturedInSectionQuery } from '$quantum/organisms/FeaturedInSection/gql';
import type { AnyObject } from '$util/types';
import { useAppContext } from '$util/AppContext';
import { getShortTextByKey } from '$cms/getDataByKey';
import { getMetaTitle } from '$components/pages/gql';
import { isPreviewActive } from '$server/modules/cms/util';
import { getLocaleFromHostname } from '$util/domains';
import { CACHE_CONTROL_CACHE_ENABLED, CACHE_CONTROL_NO_CACHE } from '../internals/next/config/headers';

const PAGE_NAME = 'about-us';

const ReassuranceBlueIconsTheme = deferHydration(() => import('$components/ReassuranceBlueIconsTheme'), {
  hydrationId: '$components/ReassuranceBlueIconsTheme',
  trigger: 'view',
});

const FeaturedInSection = deferHydration(() => import('$quantum/organisms/FeaturedInSection'), {
  hydrationId: '$quantum/organisms/FeaturedInSection',
  trigger: 'view',
});

const OurStorySection = deferHydration(() => import('$components/pages/about-us/OurStorySection'), {
  hydrationId: '$components/pages/about-us/OurStorySection',
  trigger: 'view',
});

const OurTeamSection = deferHydration(() => import('$components/pages/about-us/OurTeamSection'), {
  hydrationId: '$components/pages/about-us/OurTeamSection',
  trigger: 'view',
});

const VideoSection = deferHydration(() => import('$components/pages/about-us/VideoSection'), {
  hydrationId: '$components/pages/about-us/VideoSection',
  trigger: 'view',
});

const BioSection = deferHydration(() => import('$components/pages/about-us/BioSection'), {
  hydrationId: '$components/pages/about-us/BioSection',
  trigger: 'view',
});

const TryFixterSection = deferHydration(() => import('$components/TryFixterSection'), {
  hydrationId: '$components/TryFixterSection',
  trigger: 'view',
});

const TeamSupportSection = deferHydration(() => import('$quantum/organisms/TeamSupportSection'), {
  hydrationId: '$components/TeamSupportSection',
  trigger: 'view',
});

interface AboutUsPageProps {
  cmsContent: AnyObject;
  initChat: string | string[] | boolean;
}

function AboutUsPage(): JSX.Element {
  const AppContext = useAppContext();
  const {
    cmsContent: { dictionary },
  } = AppContext;
  const getShortText = getShortTextByKey.bind(dictionary);
  const pageTitle = getShortText('metaTitle.aboutUs', 'About us | Fixter');

  return (
    <ConfigProvider
      theme={{
        cssVar: true,
        hashed: false,
        token: { fontFamily: 'Rubik, sans-serif' },
      }}
    >
      <MainLayout
        seo={{
          noindex: false,
          nofollow: false,
          title: pageTitle,
          metaDescription:
            `Looking for hassle-free MOT, car service, repair or maintenance? ` +
            `Let Fixter take the strain! Our local experts will collect and deliver ` +
            `your car to your door and make sure you get the right job done at a fair price.`,
        }}
      >
        <TopSection />
        <ReassuranceBlueIconsTheme />
        <FeaturedInSection />
        <OurStorySection />
        <VideoSection />
        <BioSection />
        <OurTeamSection />
        <TryFixterSection />
        <TeamSupportSection source={PAGE_NAME} />
      </MainLayout>
    </ConfigProvider>
  );
}

export const getServerSideProps: GetServerSideProps<AboutUsPageProps> = async ({
  req,
  res,
  query: urlQuery,
  preview: isPreviewMode,
}) => {
  const serverParams = {
    pathname: 'about-us',
    pageType: [],
    pageTypeFromPath: 'about-us',
  };

  const locale = getLocaleFromHostname(req.headers.host || '');
  if (locale !== Locale.EN_GB) {
    return {
      notFound: true,
    };
  }

  const preview = isPreviewActive(urlQuery, isPreviewMode);
  const cmsContent = await queryCmsContent(PAGE_NAME, {
    cmsQuery: [
      GET_HOMEPAGE_OPEN_GRAPH_CONTENT,
      GET_DOMAIN_VERIFICATION_CONTENT,
      getNavbarQuery,
      getVrmLookupQuery,
      QUERY_GET_FOOTER_CMS_CONTENT,
      getReassuranceQuery,
      GET_CUSTOMER_SERVICE_CMS_QUERY,
      getTeamSupportSection,
      getTopNavBannerQuery,
      getFeaturedInSectionQuery,
      getMetaTitle,
    ],
    resQuery: [QUERY_READ_FOOTER_DATA],
    context: {
      variables: { locale },
      preview,
    },
  });

  if (preview) res.setHeader('Cache-Control', CACHE_CONTROL_NO_CACHE);
  else res.setHeader('Cache-Control', CACHE_CONTROL_CACHE_ENABLED);

  return {
    props: { serverParams, initChat: false, cmsContent },
  };
};

export default AboutUsPage;
